/**
 * CoopMissions — the single source of truth for the COOP campaign.
 *
 * Coop is a 2-player vs-NAMED-BOTS campaign: the host creates a private
 * lobby (invite code), picks a mission, and the two humans fight the
 * mission's pre-defined bots as a TEAM (their combined minions on the
 * lower half of the board vs the enemy on top).
 *
 * EVERYTHING a mission needs lives in this shared file:
 *  - the lobby description text (rendered client-side),
 *  - the mission mode:
 *      'team'   — ONE combat per round: the team (both humans) vs one bot
 *                 (the other living bots, if any, sit out);
 *      'team2'  — like 'team', but the mission has TWO bots that NEVER face
 *                 each other: the team always fights one bot while the other
 *                 sits out; both must be defeated;
 *      'normal' — a REGULAR 9-player game (2 humans + 7 bots, standard
 *                 1v1 pairing); the goal is 1st place;
 *  - the NAMED bots: display name + starting battlefield layout (class,
 *    level, the FULL keyword list, +hp/+atk bonuses on top of the base
 *    class stats) + the strategy profile id (resolved by BotProfiles —
 *    mission bots are looked up by their exact coop bot id BEFORE the
 *    bot_N rotation; mission 4's bots use the standard rotation).
 *
 * HOST PROGRESSION (sequential campaign): mission N+1 unlocks only once
 * mission N has been CLEARED AS HOST (tracked in the user database — see
 * UserDatabase.recordCoopMissionCleared). When ALL missions are cleared a
 * RANDOM mission is chosen at host time (seeded — pickRandomCoopMission).
 *
 * A mission is CLEARED when all of its bots are defeated (mission 4:
 * 1st place in the 9-player game — equivalently, all 7 bots gone).
 *
 * Adding a mission: APPEND an entry to COOP_MISSIONS. The lobby picker,
 * the DevServer (validation + clear recording) and the tests all read this
 * table — nothing else needs to change.
 */

import { MinionInstance } from './Minion';
import {
  testMinions,
  BLIGHTCASTER_MINION_DEFINITION,
  TREASURE_CHEST_MINION_DEFINITION,
  LIFE_CHEST_MINION_DEFINITION,
} from './testMinions';
import { MinionType, VALID_MINION_TYPES } from './MinionType';
import { GAME_ABILITY_DEFINITIONS } from '../../services/GameAbilities';
import { RANDOM_EVOLUTION_ABILITY_ID } from './AbilityConstants';
import {
  getEligibleAbilityUpgrades,
  abilitiesReplacedByUpgrade,
} from './UpgradePath';

// ─── Shared constants ─────────────────────────────────────────────────────

/**
 * The gold cost of GIVING a minion to your teammate (the sell zone's
 * second option — the shared source for the server validation and the
 * client UI label).
 */
export const COOP_GIVE_COST = 1;

/**
 * The PER-HUMAN board cap in a COOP team mission (5 — the combined team
 * row is 5+5 = 10 wide). The shared source for the server (createCoopMatch
 * sets PlayerState.boardSizeLimit) and the client (the two fixed halves).
 */
export const COOP_HUMAN_BOARD_SIZE = 5;

/**
 * The board cap for a COOP team-mission BOT (10 — the enemy row on top).
 */
export const COOP_BOT_BOARD_SIZE = 10;

// ─── Types ─────────────────────────────────────────────────────────────────

/** The combat shape of a coop mission. */
export type CoopMissionMode = 'team' | 'team2' | 'normal';

/**
 * One starting minion of a mission bot's battlefield layout.
 *
 * `abilities` is the minion's FULL keyword list (base abilities included —
 * e.g. a Taunt Tank that also carries Fortress lists BOTH). The bonuses
 * are applied ON TOP of the base class stats (not on top of the level's
 * stat growth — levels here express the keyword unlocks, the bonuses are
 * the mission's explicit stat gifts).
 */
export interface CoopMissionBotMinion {
  /** The minion class (a valid MinionType — one of the base five + Blightcaster). */
  minionType: MinionType;
  /** The minion's level (1-6; level = upgradeCount + 1). */
  level: number;
  /** The FULL keyword (ability) list the minion carries. */
  abilities: string[];
  /** Flat HP bonus on top of the base class HP (default 0). */
  hpBonus?: number;
  /** Flat attack bonus on top of the base class attack (default 0). */
  atkBonus?: number;
}

/** A named mission bot. */
export interface CoopMissionBot {
  /** The bot's display name (shown in the HUD, combat UI and result screens). */
  name: string;
  /**
   * The strategy profile id (resolved by BotProfiles.missionBotProfileForId
   * against the bot's exact coop id). null = the standard bot_N rotation
   * (mission 4's regular bots — they are a regular game).
   */
  profileId: string | null;
  /** The battlefield layout the bot starts the game with (in board order). */
  initialMinions: CoopMissionBotMinion[];
}

/** One campaign mission. */
export interface CoopMissionDefinition {
  /** Stable mission id (campaign order = array order). */
  id: string;
  /** 1-based campaign position (derived, pinned for the lobby UI). */
  number: number;
  /** Display name. */
  name: string;
  /** The lobby description text (the mission briefing). */
  description: string;
  /** The combat shape (see CoopMissionMode). */
  mode: CoopMissionMode;
  /** The mission's named bots. */
  bots: CoopMissionBot[];
  /**
   * TREASURE TURNS (team missions only): every {@link TREASURE_TURN_EVERY_ROUNDS}
   * th round (5, 10, 15, …) the battle phase fights a RANDOM
   * {@link TREASURE_FIGHT_SETS} collection (a board of high-level cards
   * around a Treasure Chest / Life Chest item) INSTEAD of the mission bot —
   * the bot the team would have fought sits that combat out (its board and
   * life are untouched; its cards are restored right after the fight).
   * Absent/false = no treasure turns.
   */
  treasureTurns?: boolean;
}

// ─── Treasure turns (every 5th round in opted-in team missions) ────────────

/** A treasure turn happens every N rounds (rounds N, 2N, 3N, …). */
export const TREASURE_TURN_EVERY_ROUNDS = 5;

/**
 * True when the given round of the given mission is a TREASURE TURN:
 * the mission opts in (team/team2 missions only — 'normal' never does) and
 * the round number is a multiple of TREASURE_TURN_EVERY_ROUNDS.
 */
export function isCoopTreasureRound(
  missionId: string,
  round: number,
  allMissions: readonly CoopMissionDefinition[] = COOP_MISSIONS
): boolean {
  const mission = allMissions.find((m) => m.id === missionId);
  return !!mission?.treasureTurns && round > 0 && round % TREASURE_TURN_EVERY_ROUNDS === 0;
}

/**
 * One card slot of a treasure-fight set.
 *
 * `kind: 'minion'` — a random high-level card of the class: the class's
 * base stats + base keywords, then `level - 1` upgrade slots filled in two
 * passes: the `guaranteedAbilities` FIRST (each consumes one slot, the
 * same replacement rules as a normal upgrade — Greater Heal removes Heal),
 * then the remaining slots RANDOMLY (uniform over +5 HP / +5 ATK / every
 * level-eligible keyword of the class). `kind: 'item'` — a random one of
 * the two item treasure cards (Treasure Chest / Life Chest; always level 1).
 */
export type TreasureFightEntry =
  | {
      kind: 'minion';
      minionType: MinionType;
      level: number;
      guaranteedAbilities?: string[];
    }
  | { kind: 'item' };

/** A predefined treasure-fight collection (the board the team fights). */
export interface TreasureFightSet {
  id: string;
  entries: TreasureFightEntry[];
}

/**
 * The PREDEFINED treasure-fight collections (the user's spec, set order =
 * display order). One RANDOM set is drawn per treasure turn (seeded — the
 * match's RNG). Every set carries exactly ONE item slot (the chest the
 * fight is named after): killing it pays the killer its keyword (Treasure
 * +25 gold / Life-full +1 life) through the centralized death-gold rule.
 */

// Ability ids the sets guarantee (aliases keep the spec readable).
const GREATER_HEAL_TREASURE_ID = 'greater_heal';
const RESURRECT_TREASURE_ID = 'resurrect';
const SWARMLORD_TREASURE_ID = 'swarmlord';
const WARBAND_TREASURE_ID = 'warband';

export const TREASURE_FIGHT_SETS: readonly TreasureFightSet[] = [
  {
    id: 'set1',
    entries: [
      { kind: 'minion', minionType: 'tank', level: 4 },
      { kind: 'item' },
      { kind: 'minion', minionType: 'tank', level: 4 },
      { kind: 'minion', minionType: 'healer', level: 4, guaranteedAbilities: [GREATER_HEAL_TREASURE_ID] },
    ],
  },
  {
    id: 'set2',
    entries: [
      { kind: 'minion', minionType: 'warrior', level: 4 },
      { kind: 'item' },
      { kind: 'minion', minionType: 'warrior', level: 4 },
      { kind: 'minion', minionType: 'healer', level: 4, guaranteedAbilities: [GREATER_HEAL_TREASURE_ID] },
    ],
  },
  {
    id: 'set3',
    entries: [
      { kind: 'minion', minionType: 'warrior', level: 2 },
      { kind: 'item' },
      { kind: 'minion', minionType: 'warrior', level: 2 },
      {
        kind: 'minion',
        minionType: 'healer',
        level: 6,
        guaranteedAbilities: [GREATER_HEAL_TREASURE_ID, RESURRECT_TREASURE_ID],
      },
    ],
  },
  {
    id: 'set4',
    entries: [
      { kind: 'minion', minionType: 'mage', level: 6, guaranteedAbilities: [SWARMLORD_TREASURE_ID] },
      { kind: 'item' },
      { kind: 'minion', minionType: 'bard', level: 3, guaranteedAbilities: [WARBAND_TREASURE_ID] },
    ],
  },
];

/**
 * Builds the treasure-fight board for one treasure turn: ONE random set
 * (seeded) with every card built fresh.
 *
 * Deterministic for a given (rng, round) — the ids embed the round + set
 * so successive treasure turns never collide. The caller (RoundCoordinator)
 * installs the instances on the sitting-out bot's board for the combat and
 * removes them again after it resolves (the bot's real board is restored
 * from its pre-combat snapshot).
 */
export function buildTreasureFightBoard(
  rng: { nextInt(min: number, max: number): number },
  round: number
): { instanceIds: string[]; instances: MinionInstance[] } {
  const set = TREASURE_FIGHT_SETS[rng.nextInt(0, TREASURE_FIGHT_SETS.length)];
  const instanceIds: string[] = [];
  const instances: MinionInstance[] = [];
  set.entries.forEach((entry, i) => {
    const instanceId = `treasure_r${round}_${set.id}_${i + 1}`;
    const minion =
      entry.kind === 'item'
        ? buildTreasureItem(instanceId, rng)
        : buildTreasureMinion(instanceId, entry, rng);
    instanceIds.push(instanceId);
    instances.push(minion);
  });
  return { instanceIds, instances };
}

/** The item slot: a random one of the two chest items (always level 1). */
function buildTreasureItem(instanceId: string, rng: { nextInt(min: number, max: number): number }): MinionInstance {
  const def = rng.nextInt(0, 2) === 0 ? TREASURE_CHEST_MINION_DEFINITION : LIFE_CHEST_MINION_DEFINITION;
  return new MinionInstance(
    instanceId,
    def.id,
    def.startingHp,
    def.attack,
    0, // items are always level 1
    'base',
    [...def.baseAbilities],
    def.minionType
  );
}

/**
 * A random high-level card of the entry's class: base stats + base
 * keywords, guaranteed keywords first (one upgrade slot each, replacement
 * rules applied), then the remaining slots rolled uniformly over
 * {+5 HP, +5 ATK, every level-eligible keyword} (the shared upgrade
 * eligibility — Random Evolution is excluded: a treasure card rolls a
 * concrete keyword or a stat boost, never the meta-grant).
 */
function buildTreasureMinion(
  instanceId: string,
  entry: Extract<TreasureFightEntry, { kind: 'minion' }>,
  rng: { nextInt(min: number, max: number): number }
): MinionInstance {
  const def = baseDefinitionForCoopType(entry.minionType);
  let hp = def.startingHp;
  let attack = def.attack;
  const owned = [...def.baseAbilities];
  let upgradeCount = 0;

  const grantAbility = (abilityId: string): void => {
    // Replacement rules (Greater Heal removes Heal, …) — the same shared
    // rule the real upgrade path applies.
    for (const removed of abilitiesReplacedByUpgrade(abilityId)) {
      const idx = owned.indexOf(removed);
      if (idx !== -1) owned.splice(idx, 1);
    }
    if (!owned.includes(abilityId)) owned.push(abilityId);
    upgradeCount++;
  };

  // Guaranteed keywords first (a base keyword is already owned — no slot).
  for (const abilityId of entry.guaranteedAbilities ?? []) {
    if (owned.includes(abilityId)) continue;
    grantAbility(abilityId);
  }

  // The remaining upgrade slots: random stat boosts / keywords.
  const remaining = entry.level - 1 - upgradeCount;
  for (let i = 0; i < remaining; i++) {
    const level = upgradeCount + 1;
    const eligible = getEligibleAbilityUpgrades(
      entry.minionType,
      owned,
      level,
      GAME_ABILITY_DEFINITIONS,
      { grandmasterCount: 0 }
    ).filter((d) => d.id !== RANDOM_EVOLUTION_ABILITY_ID);
    const poolSize = 2 + eligible.length; // +5 HP, +5 ATK, …keywords
    const pick = rng.nextInt(0, poolSize);
    if (pick === 0) hp += 5;
    else if (pick === 1) attack += 5;
    else grantAbility(eligible[pick - 2].id);
    // EVERY branch is one upgrade (level = upgradeCount + 1 — the real
    // game's rule: stat boosts and keyword grants both consume a slot).
    if (pick === 0 || pick === 1) upgradeCount++;
  }

  return new MinionInstance(
    instanceId,
    def.id,
    hp,
    attack,
    upgradeCount,
    'base',
    owned,
    def.minionType
  );
}

// ─── The mission catalogue (APPEND new missions here) ─────────────────────

export const COOP_MISSIONS: readonly CoopMissionDefinition[] = [
  {
    id: 'the_blight',
    number: 1,
    name: 'The Blight',
    description:
      'A rot creeps out of the marshes. At its heart stands The Blight, a ' +
      'Blightcaster whose toxic clouds poison everything that breathes, ' +
      'guarded by a bulwark of a fortress tank. Bring the blight down before ' +
      'it spreads — together. Every fifth turn the blight drops a treasure ' +
      'hoard: fight the chest and its guard, keep what it pays.',
    mode: 'team',
    treasureTurns: true,
    bots: [
      {
        name: 'The Blight',
        profileId: 'coop_festergore',
        initialMinions: [
          {
            minionType: 'blightcaster',
            level: 4,
            abilities: ['toxic_cloud', 'blunt'],
            hpBonus: 10,
          },
          {
            minionType: 'tank',
            level: 3,
            abilities: ['shield', 'fortress'],
            hpBonus: 5,
          },
        ],
      },
    ],
  },
  {
    id: 'sirens_song',
    number: 2,
    name: "Siren's Song",
    description:
      'Siri the Siren lures her prey to a wall of sound: a healing voice, a ' +
      'buff-singing bard and a fortress of muscle. Her board is always full ' +
      'and always stronger. Break her chorus before it swallows you whole. ' +
      'Every fifth turn she sings over a treasure hoard — take it while her ' +
      'guard looks away.',
    mode: 'team',
    treasureTurns: true,
    bots: [
      {
        name: 'Siri the Siren',
        profileId: 'coop_seren',
        initialMinions: [
          {
            minionType: 'healer',
            level: 3,
            abilities: ['heal', 'lifesteal'],
            atkBonus: 5,
          },
          {
            minionType: 'bard',
            level: 4,
            abilities: ['buff_vitality', 'buff_attack'],
            hpBonus: 10,
          },
          {
            minionType: 'tank',
            level: 3,
            abilities: ['shield', 'fortress'],
            hpBonus: 5,
          },
        ],
      },
    ],
  },
  {
    id: 'death_undone',
    number: 3,
    name: 'Death Undone',
    description:
      'DANGAR cheated death once and has not stopped since. His grave ' +
      'crawls with the undead — an exploding mage feeds a deathless tank, ' +
      'and every corpse raised makes the horde stronger. Unmake his curse ' +
      'and end his forever. Every fifth turn a treasure hoard crawls out of ' +
      'his graves — claim it before his curse claims you.',
    mode: 'team',
    treasureTurns: true,
    bots: [
      {
        name: 'DANGAR',
        profileId: 'coop_morgath',
        initialMinions: [
          {
            minionType: 'tank',
            level: 6,
            abilities: ['fortress', 'brute', 'deathfeeder'],
            hpBonus: 15,
          },
          {
            minionType: 'mage',
            level: 5,
            abilities: ['explosive', 'gravedigger'],
            atkBonus: 15,
          },
        ],
      },
    ],
  },
  {
    id: 'chicken_dinner',
    number: 4,
    name: 'Chicken Dinner',
    description:
      'No script, no mercy: a nine-player gauntlet with seven rivals and ' +
      'your partner. It is a regular fight to the last — claim 1st place ' +
      'and dinner is yours.',
    mode: 'normal',
    bots: [
      { name: 'Rook', profileId: null, initialMinions: [] },
      { name: 'Bramble', profileId: null, initialMinions: [] },
      { name: 'Grimjaw', profileId: null, initialMinions: [] },
      { name: 'Whisper', profileId: null, initialMinions: [] },
      { name: 'Cinder', profileId: null, initialMinions: [] },
      { name: 'Thornback', profileId: null, initialMinions: [] },
      { name: 'Vex', profileId: null, initialMinions: [] },
    ],
  },
  {
    id: 'hive_mind',
    number: 5,
    name: 'Hive Mind',
    description:
      'Two guardians share one will: Midas, an unbreakable living ' +
      'fortress, and Diner, whose death would loose a swarm ' +
      'of glass cannon drones. They never fight among themselves — they ' +
      'fight YOU. Defeat both to silence the hive. Every fifth turn the hive ' +
      'buries a treasure hoard — its guardians sit out while you dig.',
    mode: 'team2',
    treasureTurns: true,
    bots: [
      {
        name: 'Midas',
        profileId: 'coop_queen_hive',
        initialMinions: [
          {
            minionType: 'tank',
            level: 6,
            abilities: ['fortress'],
            hpBonus: 25,
          },
        ],
      },
      {
        name: 'Diner',
        profileId: 'coop_swarm_priestess',
        initialMinions: [
          {
            minionType: 'mage',
            level: 6,
            abilities: ['swarmlord'],
            atkBonus: 20,
          },
        ],
      },
    ],
  },
];

// ─── Lookup helpers ────────────────────────────────────────────────────────

/** Returns the mission definition for the given id, or null. */
export function getCoopMissionById(id: string): CoopMissionDefinition | null {
  return COOP_MISSIONS.find((m) => m.id === id) ?? null;
}

/** True for a valid coop mission id. */
export function isCoopMissionId(id: string): boolean {
  return COOP_MISSIONS.some((m) => m.id === id);
}

/** The mission's position in the campaign (1-based), or null. */
export function getCoopMissionNumber(id: string): number | null {
  return getCoopMissionById(id)?.number ?? null;
}

/**
 * The coop bot id for one slot of a mission: `coop_<missionId>_<n>`
 * (n = 1-based bot index). These ids are deterministic per mission —
 * the profile lookup (BotProfiles.missionBotProfileForId) and the
 * display-name lookup both key off them.
 */
export function coopBotId(missionId: string, botIndex: number): string {
  return `coop_${missionId}_${botIndex + 1}`;
}

/**
 * The display name for a coop bot id (null for non-coop ids).
 */
export function coopBotDisplayName(playerId: string): string | null {
  const m = /^coop_(.+)_(\d+)$/.exec(playerId);
  if (!m) return null;
  const mission = getCoopMissionById(m[1]);
  const bot = mission?.bots[parseInt(m[2], 10) - 1];
  return bot?.name ?? null;
}

/**
 * The display names of REGULAR (queue-filled) bots: the internal `bot_N`
 * id is not user-facing — bot_1 is always "Grumble the Oaf", bot_2 always
 * "Siri the Siren", ... (the BotFillService's counter restarts per fill
 * cycle, so a fixed index→name map gives every regular bot a stable,
 * readable identity in the pills, combat labels, scoreboard and winner
 * screen — all of which render PlayerData.displayName ?? id). The pool
 * covers a full 8-player table (at most 7 bots).
 */
export const REGULAR_BOT_DISPLAY_NAMES: string[] = [
  'Grumble the Oaf',
  'Siri the Siren',
  'Morg the Gravekeeper',
  'Pip the Trickster',
  'Bramble the Elder',
  'Vex the Swift',
  'Olla the Stormcaller',
  'Tibb the Unlucky',
];

/** The display name for a regular `bot_N` player id (null for humans). */
export function regularBotDisplayName(playerId: string): string | null {
  const m = /^bot_(\d+)$/.exec(playerId);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return REGULAR_BOT_DISPLAY_NAMES[(n - 1) % REGULAR_BOT_DISPLAY_NAMES.length];
}

// ─── Combat side (left/right) ─────────────────────────────────────────────

/**
 * The host's chosen COMBAT SIDE in a coop lobby: which half of the
 * combined team row THEIR minions occupy (left → teamOrder 'AB', the
 * host's copies first/left and first to attack; right → 'BA'). The
 * teammate automatically gets the OTHER side. Lets the players decide
 * whose minions attack first (previously a seeded coin flip).
 */
export type CoopSide = 'left' | 'right';

export function isCoopSide(value: unknown): value is CoopSide {
  return value === 'left' || value === 'right';
}

// ─── Host progression (sequential campaign) ────────────────────────────────

/**
 * The missions AVAILABLE to a host given the list of mission ids they have
 * CLEARED AS HOST:
 *  - mission 1 is always available;
 *  - mission N+1 unlocks only once mission N is cleared;
 *  - when ALL missions are cleared, EVERY mission is available (the "no
 *    missions left" state — a random one is then chosen at host time).
 *
 * Pure and deterministic — the shared single source the lobby service
 * (server), the lobby UI (client picker) and the tests read.
 */
export function availableCoopMissionsForHost(
  clearedIds: readonly string[],
  allMissions: readonly CoopMissionDefinition[] = COOP_MISSIONS
): string[] {
  const cleared = new Set(clearedIds.filter((id) => isCoopMissionId(id)));
  // All cleared → everything is selectable (random pick at host time).
  if (allMissions.every((m) => cleared.has(m.id))) {
    return allMissions.map((m) => m.id);
  }
  const available: string[] = [];
  for (const mission of allMissions) {
    available.push(mission.id);
    // The NEXT mission unlocks only once THIS one is cleared.
    if (!cleared.has(mission.id)) break;
  }
  return available;
}

/**
 * Picks the lobby's mission at host time (server-side, authoritative):
 *  - while the campaign is NOT fully cleared: the host's NEXT uncleared
 *    mission (the sequential unlock) — the lobby default the host may
 *    still change among their available ones;
 *  - when ALL missions are cleared: a RANDOM mission (seeded RNG — the
 *    "no available missions" rule).
 */
export function pickHostMission(
  clearedIds: readonly string[],
  rng: { nextInt(min: number, max: number): number },
  allMissions: readonly CoopMissionDefinition[] = COOP_MISSIONS
): string {
  const available = availableCoopMissionsForHost(clearedIds, allMissions);
  if (allMissions.every((m) => available.includes(m.id))) {
    // All cleared — random mission (seeded).
    return available[rng.nextInt(0, available.length)];
  }
  // Sequential: the first uncleared mission.
  return available[available.length - 1];
}

/**
 * The host's NEXT mission to complete (the first uncleared mission in
 * campaign order) — the one the lobby UI marks clearly in the mission
 * dropdown. null when everything is cleared (every mission is a replay;
 * none is "next").
 */
export function nextCoopMissionForHost(
  clearedIds: readonly string[],
  allMissions: readonly CoopMissionDefinition[] = COOP_MISSIONS
): string | null {
  const cleared = new Set(clearedIds.filter((id) => isCoopMissionId(id)));
  for (const mission of allMissions) {
    if (!cleared.has(mission.id)) return mission.id;
  }
  return null;
}

// ─── Bot layout construction ──────────────────────────────────────────────

/**
 * Resolves the base class definition for a layout minion's class.
 * The campaign bots use the base five classes + the Blightcaster (the
 * locked Buffer/Runekeeper/Swarmer classes never appear in a layout).
 */
export function baseDefinitionForCoopType(minionType: MinionType) {
  if (minionType === 'blightcaster') {
    return BLIGHTCASTER_MINION_DEFINITION;
  }
  const def = testMinions.find((d) => d.minionType === minionType);
  if (!def) {
    throw new Error(`Unknown coop layout class: "${minionType}"`);
  }
  return def;
}

/**
 * Builds a mission bot's starting minion instance from a layout entry:
 * the base class stats + the entry's flat bonuses, the entry's FULL keyword
 * list, and level = 1 + (level - 1) upgrades. The instance is NOT placed on
 * a battlefield (the caller assigns positions).
 *
 * Deterministic for a given (instanceId, entry) — mission matches are
 * reproducible per match seed.
 */
export function buildCoopBotMinion(
  instanceId: string,
  entry: CoopMissionBotMinion
): MinionInstance {
  const def = baseDefinitionForCoopType(entry.minionType);
  const hp = def.startingHp + (entry.hpBonus ?? 0);
  const attack = def.attack + (entry.atkBonus ?? 0);
  return new MinionInstance(
    instanceId,
    def.id,
    hp,
    attack,
    entry.level - 1, // level = upgradeCount + 1
    'base',
    [...entry.abilities],
    def.minionType
  );
}

// ─── Spec validation (module-load time — a malformed spec crashes early) ───

const KNOWN_ABILITY_IDS = new Set(GAME_ABILITY_DEFINITIONS.map((d) => d.id));

COOP_MISSIONS.forEach((mission) => {
  if (!/^[a-z0-9_]+$/.test(mission.id)) {
    throw new Error(`Coop mission id must be snake_case: "${mission.id}"`);
  }
  if (mission.bots.length === 0) {
    throw new Error(`Coop mission "${mission.id}" needs at least one bot`);
  }
  if (mission.mode === 'team' && mission.bots.length !== 1) {
    throw new Error(`Coop mission "${mission.id}" (mode 'team') needs exactly one bot`);
  }
  if (mission.mode === 'team2' && mission.bots.length !== 2) {
    throw new Error(`Coop mission "${mission.id}" (mode 'team2') needs exactly two bots`);
  }
  if (mission.mode === 'normal' && mission.bots.length !== 7) {
    throw new Error(`Coop mission "${mission.id}" (mode 'normal') needs exactly seven bots`);
  }
  mission.bots.forEach((bot, i) => {
    if (!bot.name) {
      throw new Error(`Coop mission "${mission.id}" bot ${i + 1} needs a name`);
    }
    bot.initialMinions.forEach((entry, j) => {
      if (!VALID_MINION_TYPES.includes(entry.minionType)) {
        throw new Error(
          `Coop mission "${mission.id}" bot "${bot.name}" minion ${j + 1}: ` +
          `unknown class "${entry.minionType}"`
        );
      }
      if (entry.level < 1 || entry.level > 6) {
        throw new Error(
          `Coop mission "${mission.id}" bot "${bot.name}" minion ${j + 1}: ` +
          `level ${entry.level} out of range 1-6`
        );
      }
      for (const ability of entry.abilities) {
        if (!KNOWN_ABILITY_IDS.has(ability)) {
          throw new Error(
            `Coop mission "${mission.id}" bot "${bot.name}" minion ${j + 1}: ` +
            `unknown ability "${ability}"`
          );
        }
      }
      if ((entry.hpBonus ?? 0) < 0 || (entry.atkBonus ?? 0) < 0) {
        throw new Error(
          `Coop mission "${mission.id}" bot "${bot.name}" minion ${j + 1}: ` +
          `bonuses must be non-negative`
        );
      }
    });
  });
});

// Uniqueness pins (campaign invariants).
const missionIds = COOP_MISSIONS.map((m) => m.id);
if (new Set(missionIds).size !== missionIds.length) {
  throw new Error('Coop mission ids must be unique');
}
COOP_MISSIONS.forEach((m, i) => {
  if (m.number !== i + 1) {
    throw new Error(`Coop mission numbers must be sequential (mission "${m.id}" has ${m.number})`);
  }
});

// Treasure-set pins (spec invariants — a malformed set crashes module load).
const treasureSetIds = TREASURE_FIGHT_SETS.map((s) => s.id);
if (new Set(treasureSetIds).size !== treasureSetIds.length) {
  throw new Error('Treasure fight set ids must be unique');
}
TREASURE_FIGHT_SETS.forEach((set) => {
  if (set.entries.length === 0) {
    throw new Error(`Treasure set "${set.id}" needs at least one card`);
  }
  const itemCount = set.entries.filter((e) => e.kind === 'item').length;
  if (itemCount !== 1) {
    throw new Error(
      `Treasure set "${set.id}" must carry exactly ONE item slot (the chest) — got ${itemCount}`
    );
  }
  set.entries.forEach((entry, j) => {
    if (entry.kind === 'item') return;
    if (!VALID_MINION_TYPES.includes(entry.minionType) || entry.minionType === 'item') {
      throw new Error(
        `Treasure set "${set.id}" card ${j + 1}: unknown class "${entry.minionType}"`
      );
    }
    if (entry.level < 1 || entry.level > 6) {
      throw new Error(
        `Treasure set "${set.id}" card ${j + 1}: level ${entry.level} out of range 1-6`
      );
    }
    const guaranteed = entry.guaranteedAbilities ?? [];
    if (new Set(guaranteed).size !== guaranteed.length) {
      throw new Error(
        `Treasure set "${set.id}" card ${j + 1}: duplicate guaranteed ability`
      );
    }
    if (guaranteed.length > entry.level - 1) {
      throw new Error(
        `Treasure set "${set.id}" card ${j + 1}: ${guaranteed.length} guaranteed abilities don't fit level ${entry.level} (${entry.level - 1} upgrade slots)`
      );
    }
    for (const ability of guaranteed) {
      const def = GAME_ABILITY_DEFINITIONS.find((d) => d.id === ability);
      if (!def) {
        throw new Error(
          `Treasure set "${set.id}" card ${j + 1}: unknown guaranteed ability "${ability}"`
        );
      }
      if (!def.types.includes(entry.minionType)) {
        throw new Error(
          `Treasure set "${set.id}" card ${j + 1}: guaranteed ability "${ability}" is not a "${entry.minionType}" keyword`
        );
      }
      if (def.availableAtLevel > entry.level) {
        throw new Error(
          `Treasure set "${set.id}" card ${j + 1}: guaranteed ability "${ability}" unlocks at level ${def.availableAtLevel} (card is level ${entry.level})`
        );
      }
    }
  });
});
// Only TEAM missions may opt in (normal missions have no team combat to alter).
COOP_MISSIONS.forEach((m) => {
  if (m.treasureTurns && m.mode === 'normal') {
    throw new Error(
      `Coop mission "${m.id}" (mode 'normal') cannot have treasure turns`
    );
  }
});
